<template lang="pug">
AppDialog.job-dialog(
  :title="`${editing ? 'Edit' : 'Add'} job`"
  :class="{ edit: editing }"
  show
  append-to-body
  :is-dismissable="canDismiss"
  @close="closeWizard"
)
  ValidationObserver(
    v-slot="{ invalid }"
    ref="dialogObserver"
    slim
  )
    ScheduleJobStep(
      :reset="!show"
      :start-week-on="startWeekOn"
      :job="job"
      :form="form"
      :editing="editing"
      :has-footer="false"
      :timezone="timeZone"
      :time-zone-abbr="timeZoneAbbr"
      :work-order="workOrder || {}"
      :select2-options="select2Options"
      :is-job-in-the-past="isJobInThePast"
      :all-assignees="activeOrganizationMembers"
    )
      .job-dialog__footer.modal-footer.d-flex.align-items-center.px-0.pb-0(
        :class="{ 'justify-content-end': !editing, 'justify-content-between': editing }"
      )
        template(v-if="!editing")
          AppButton.mr-sm-10.mr-0(@click="closeWizard") Cancel
          AppButton.ml-sm-10.mt-0(
            type="primary"
            :disabled="invalid || form.processing"
            @click="onJobSave"
          ) Save
        template(v-else)
          AppButton.mt-0(
            type="danger"
            :disabled="invalid || form.processing"
            @click="deleteJobAndCloseDialog"
          ) Delete
          div
            AppButton.mr-10(@click="closeWizard") Cancel
            AppButton.ml-10.mt-0(
              type="primary"
              :disabled="invalid || form.processing"
              @click="onJobSave"
            ) Save
</template>

<script>
import { JOB_REPEAT_TYPE, WORK_ORDERS_URL } from '~/app_constants';
import { negate, isNil, omit, pick, pickBy, isEqual } from 'lodash-es';
import { RecurringJobs, WorkOrderJobActions } from '~/mixins';
import { recurringJobsDialogEvents } from '~/utils/work_orders';
import DialogToasterMixin from '~/mixins/WorkOrders/DialogToasterMixin';
import { formatInternalDate, formatInternalTime } from '~/utils/time_utils';
import ScheduleJobStep from './ScheduleJobStep.vue';
import WorkOrdersEditPastJobConfirmDialog from '../WorkOrdersEditPastJobConfirmDialog.vue';
import WorkOrdersConfirmDeleteRecurringJobsDialog from '~/components/work_orders/WorkOrdersConfirmDeleteRecurringJobsDialog.vue';
import WorkOrdersEditRecurringJobDialog from '../WorkOrdersEditRecurringJobDialog.vue';
import WorkOrdersEditRecurringJobSettingsDialog from '../WorkOrdersEditRecurringJobSettingsDialog.vue';
import { applyTemplate } from '~/utils/url_template';

const pastEditDialogName = WorkOrdersEditPastJobConfirmDialog.name;
const editDialogName = WorkOrdersEditRecurringJobDialog.name;
const editSettingsDialogName = WorkOrdersEditRecurringJobSettingsDialog.name;
const confirmDeleteRecurringJobsDialog = WorkOrdersConfirmDeleteRecurringJobsDialog.name;

export const defaultSchedule = () => ({
  interval: 1,
  custom: false,
  weekdays: null,
  monthday: null,
  monthweekday: null,
  monthweekdaynum: null,
  repeat_type: JOB_REPEAT_TYPE.Once,
});

export default {
  name: 'JobsDialog',
  components: {
    ScheduleJobStep,
  },
  mixins: [
    DialogToasterMixin({
      updateMessage: 'Job successfully updated',
      createMessage: 'Job successfully created',
    }),
    WorkOrderJobActions,
    RecurringJobs,
  ],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    job: {
      type: Object,
      default: () => ({}),
    },
    timeZone: {
      type: String,
      required: true,
    },
    timeZoneAbbr: {
      type: String,
      required: true,
    },
    realOrgTimeZone: {
      type: String,
      required: true,
    },
    workOrder: {
      type: Object,
      required: true,
    },
    startWeekOn: {
      type: Number,
      required: true,
    },
    activeOrganizationMembers: {
      type: Array,
      required: true,
    },
  },
  data() {
    const now = this.timeZone ? moment.tz(this.timeZone) : moment();
    const startsAt = now.clone().hours(9).minutes(0).seconds(0);
    const stopsAt = now.clone().hours(17).minutes(0).seconds(0);

    return {
      tab: 0,
      canDismiss: true,
      form: this.$inertia.form({
        details: this.editing ? this.job.details : null,
        stopsAt: this.editing ? this.job.stopsAt : stopsAt,
        startsAt: this.editing ? this.job.startsAt : startsAt,
        schedule: this.editing ? { ...this.job.schedule } : defaultSchedule(),
        assignees: this.editing ? this.job.assignees.map((assignee) => assignee.id) : [],

        userHasChangedSchedule: false,
      }),
    };
  },
  computed: {
    mapOptions() {
      return {
        zoomControl: false,
        mapTypeControl: false,
        disableDefaultUI: true,
      };
    },
    select2Options() {
      return {
        width: '100%',
      };
    },
    isJobInThePast() {
      if (this.editing && this.job) {
        const startsAt = this.job.startsAt || this.job.starts_at;
        const now = this.timeZone ? moment().tz(this.timeZone) : moment();

        return moment(startsAt).isBefore(now, 'minute');
      }

      return false;
    },
  },
  methods: {
    closeWizard() {
      if (!this.form.processing) {
        this.$emit('close');
      }
    },
    closeViewJob() {
      // This will only be available really in the Show page for the work orders
      this.$emit('close-view-job');
    },
    emitProcessing() {
      this.$emit('processing');
    },

    /**
     * Form handling
     */
    hasChangedRecurrentConfig() {
      return !isEqual(this.job.schedule, this.form.schedule);
    },
    hasChangedJobDate() {
      const valueDate = formatInternalDate(this.form.startsAt);
      const originalValueDate = formatInternalDate(this.job.startsAt);

      return valueDate !== originalValueDate;
    },
    hasChangedJobBase() {
      const jobBaseKeys = ['details', 'assignees', 'startsAt', 'stopsAt'];

      return jobBaseKeys.some((key) => {
        let value = this.form[key];
        let originalValue = this.job[key];

        if (key === 'assignees') {
          value = (value || []).map((id) => Number(id));
          originalValue = (originalValue || []).map(({ id }) => Number(id));
        }

        if (moment.isMoment(value)) {
          const valueTime = formatInternalTime(value);
          const originalValueTime = formatInternalTime(originalValue);

          return valueTime !== originalValueTime;
        }

        return !isEqual(value, originalValue);
      });
    },
    async deleteJobAndCloseDialog() {
      this.emitProcessing();

      const response = await this.confirmDeleteJob(this.job);

      if (response) {
        this.closeWizard();
        // If we have the View/Show job dialog open, close it.
        this.closeViewJob();
        recurringJobsDialogEvents.close(confirmDeleteRecurringJobsDialog);
      }
    },
    async onJobSave() {
      if (this.editing) {
        await this.onJobUpdate({
          jobId: this.job.id,
          scheduleId: this.job.schedule.id,
        });

        return;
      }

      await this.onJobCreate();
    },
    async onJobCreate() {
      const url = applyTemplate(`${WORK_ORDERS_URL}/jobs`, {
        organizationId: this.organization.id,
        woId: this.workOrder.id,
      });

      this.preparePayload(false, null);

      await this.form.post(url, {
        onSuccess: () => {
          this.closeWizard();
        },
      });
    },
    async onJobUpdate(updateParams) {
      if (this.jobIsRecurrent(this.job)) {
        const dialogName =
          this.hasChangedJobDate() || this.hasChangedJobBase()
            ? editDialogName
            : this.hasChangedRecurrentConfig()
            ? editSettingsDialogName
            : null;

        if (dialogName) {
          await this.performJobUpdate(updateParams, dialogName);
        } else {
          // No changes were made, so close the dialog
          // and let the user know everything went fine.
          this.afterActionToast(true);
        }
      } else {
        if (this.hasChangedJobBase() || this.hasChangedJobDate()) {
          await this.performJobUpdate(updateParams, null);
        } else {
          // No changes were made, so close the dialog
          // and let the user know everything went fine.
          this.afterActionToast(true);
        }
      }
    },
    async performJobUpdate(updateParams, dialogName = null) {
      // If we're dealing with a past job, just inform the user and this will tell
      // the action to use the old endpoint insteas of the new one.
      const warningModal = this.jobIsRecurrent(this.job) && this.isJobInThePast ? pastEditDialogName : dialogName;

      // This will inform the "common action" which endpoint to call
      updateParams.isJobInThePast = this.isJobInThePast;

      if (warningModal) {
        return new Promise((resolve) => {
          recurringJobsDialogEvents.open(warningModal, {
            dialogEvents: {
              cancel: () => {
                recurringJobsDialogEvents.close(warningModal);

                resolve({ success: false, confirmed: false });
              },
              confirm: async (event) => {
                updateParams.isAllSelected = isNil(event) ? true : event;

                resolve(await this.updateJob(updateParams));

                recurringJobsDialogEvents.close(warningModal);
              },
            },
          });
        });
      }

      updateParams.isAllSelected = !this.jobIsRecurrent(this.job);

      await this.updateJob(updateParams);
    },
    async updateJob({ jobId, scheduleId, isAllSelected, isJobInThePast }) {
      const isSingleUpdate = isJobInThePast || !isAllSelected;
      const jobOrSchedulesSuffix = isSingleUpdate ? 'jobs/:jobId' : 'job_schedules/:scheduleId';
      const url = applyTemplate(`${WORK_ORDERS_URL}/${jobOrSchedulesSuffix}`, {
        organizationId: this.organization.id,
        woId: this.workOrder.id,
        scheduleId,
        jobId,
      });

      this.preparePayload(true, isAllSelected);

      await this.form.put(url, {
        onSuccess: () => {
          this.closeWizard();
        },
      });
    },
    afterActionToast(success) {
      this.toast(success);
      if (success) {
        this.closeWizard();
      }
    },
    preparePayload(editing, isAllSelected) {
      this.form.transform(({ startsAt, stopsAt, details, assignees, schedule }) => {
        const orgStartsAt = this.realOrgTimeZone ? startsAt.clone().tz(this.realOrgTimeZone) : startsAt;
        const orgStopsAt = this.realOrgTimeZone ? stopsAt.clone().tz(this.realOrgTimeZone) : stopsAt;
        let data = {
          details,
          assignee_ids: assignees,
          date: formatInternalDate(orgStartsAt),
          stop_time: formatInternalTime(orgStopsAt),
          start_time: formatInternalTime(orgStartsAt),
        };

        // We only want to include the schedule if:
        // - user is creating the job and they filled in the schedule
        // - user is editing the job, they change the schedule and they want to change "all jobs"
        if ((editing && schedule && isAllSelected) || (!editing && schedule)) {
          // We need to omit the `date` otherwise we will overwrite the
          // form's date with the one from the schedule.
          const scheduleWithoutDate = omit(schedule, ['date']);

          data = {
            ...data,
            // Just pick the fields that have been filled
            ...pickBy(scheduleWithoutDate, negate(isNil)),
            // Required fields (should be handled by the previous, but MAKE sure)
            ...pick(scheduleWithoutDate, ['repeat_type', 'custom']),
          };
        }

        return data;
      });
    },
  },
};
</script>
